import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import {
  useCreateMaterialGroupHook,
  useUpdateMaterialGroupHook,
} from "../../../hooks/useMaterialHook";
import { useInstituteContext } from "../../../context/institute.context";
import { logger } from "../../../utils/common";
import {
  materialCategoryOptions,
  materialConditionOptions,
  materialStatusOptions,
  formatLabel,
} from "./material.helpers";
import {
  TMaterialCategory,
  TMaterialCondition,
  TMaterialGroup,
  TMaterialStatus,
} from "../../../apis/material/material.interfaces";

const materialGroupSchema = z.object({
  name: z
    .string()
    .min(1, "Name is required")
    .max(200, "Name must not exceed 200 characters"),
  description: z
    .string()
    .max(2000, "Description must not exceed 2000 characters")
    .optional()
    .or(z.literal("")),
  category: z.string().min(1, "Category is required"),
  status: z.string().min(1, "Status is required"),
  condition: z.string().min(1, "Condition is required"),
  location: z.string().max(200).optional().or(z.literal("")),
  department: z.string().max(200).optional().or(z.literal("")),
  supplier: z.string().max(200).optional().or(z.literal("")),
  notes: z.string().max(2000).optional().or(z.literal("")),
  quantity: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => {
      if (v === undefined || v === "" || v === null) return undefined;
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : undefined;
    }),
  minimumQuantity: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => {
      if (v === undefined || v === "" || v === null) return undefined;
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : undefined;
    }),
  cost: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => {
      if (v === undefined || v === "" || v === null) return undefined;
      const n = typeof v === "number" ? v : Number(v);
      return Number.isFinite(n) ? n : undefined;
    }),
});

type MaterialGroupFormData = z.input<typeof materialGroupSchema>;

interface ManageMaterialGroupDialogProps {
  open: boolean;
  onClose: () => void;
  group?: TMaterialGroup | null;
}

const ManageMaterialGroupDialog: FC<ManageMaterialGroupDialogProps> = ({
  open,
  onClose,
  group,
}) => {
  const isEdit = !!group;
  const { selectedInstitute } = useInstituteContext();
  const createMutation = useCreateMaterialGroupHook();
  const updateMutation = useUpdateMaterialGroupHook();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<MaterialGroupFormData>({
    resolver: zodResolver(materialGroupSchema),
    defaultValues: {
      name: "",
      description: "",
      category: "books",
      status: "available",
      condition: "good",
      location: "",
      department: "",
      supplier: "",
      notes: "",
      quantity: "",
      minimumQuantity: "",
      cost: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: group?.name ?? "",
        description: group?.description ?? "",
        category: (group?.category as string) ?? "books",
        status: (group?.status as string) ?? "available",
        condition: (group?.condition as string) ?? "good",
        location: group?.location ?? "",
        department: group?.department ?? "",
        supplier: group?.supplier ?? "",
        notes: group?.notes ?? "",
        quantity: group?.quantity ?? "",
        minimumQuantity: group?.minimumQuantity ?? "",
        cost: group?.cost ?? "",
      });
    }
  }, [open, group, reset]);

  const onSubmit = async (data: MaterialGroupFormData) => {
    const parsed = materialGroupSchema.parse(data);
    if (!selectedInstitute && !isEdit) {
      logger.show_error(
        "Please select an institute from the dashboard before creating a material group.",
      );
      return;
    }

    try {
      if (isEdit && group) {
        await updateMutation.mutateAsync({
          materialGroupId: group.id,
          request: {
            name: parsed.name,
            description: parsed.description || undefined,
            category: parsed.category as TMaterialCategory,
            status: parsed.status as TMaterialStatus,
            condition: parsed.condition as TMaterialCondition,
            location: parsed.location || undefined,
            department: parsed.department || undefined,
            supplier: parsed.supplier || undefined,
            notes: parsed.notes || undefined,
            quantity: parsed.quantity,
            minimumQuantity: parsed.minimumQuantity,
            cost: parsed.cost,
          },
        });
        logger.show_success("Material group updated successfully!");
      } else {
        await createMutation.mutateAsync({
          instituteId: selectedInstitute!.id,
          name: parsed.name,
          description: parsed.description || undefined,
          category: parsed.category as TMaterialCategory,
          status: parsed.status as TMaterialStatus,
          condition: parsed.condition as TMaterialCondition,
          location: parsed.location || undefined,
          department: parsed.department || undefined,
          supplier: parsed.supplier || undefined,
          notes: parsed.notes || undefined,
          quantity: parsed.quantity,
          minimumQuantity: parsed.minimumQuantity,
          cost: parsed.cost,
        });
        logger.show_success("Material group created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : "Failed to save material group. Please try again.",
      );
    }
  };

  const toOption = (key: string) => ({ key, value: formatLabel(key) });

  const statusOptions = materialStatusOptions.map((s) => toOption(s));
  const categoryOptions = materialCategoryOptions.map((c) => toOption(c));
  const conditionOptions = materialConditionOptions.map((c) => toOption(c));

  const activeMutation = isEdit ? updateMutation : createMutation;
  const isLoading = isSubmitting || activeMutation.isPending;
  const isError = activeMutation.isError;
  const errorMessage = activeMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-material-group-dialog"
    >
      <DialogTitle data-testid="manage-material-group-dialog-title">
        {isEdit ? "Edit Material Group" : "Add New Material Group"}
      </DialogTitle>
      <form
        onSubmit={handleSubmit(onSubmit)}
        data-testid="material-group-form"
      >
        <DialogContent data-testid="material-group-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="material-group-form-error"
            >
              {errorMessage || "Failed to save material group. Please try again."}
            </Alert>
          )}

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <ControlledTextField
              control={control}
              name="name"
              id="material-group-name"
              label="Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "material-group-name-input" }}
            />
            <ControlledSelectField
              control={control}
              name="category"
              id="material-group-category"
              label="Category"
              isRequired
              itemList={categoryOptions}
              errorMessage={errors.category?.message}
            />
            <ControlledSelectField
              control={control}
              name="status"
              id="material-group-status"
              label="Status"
              isRequired
              itemList={statusOptions}
              errorMessage={errors.status?.message}
            />
            <ControlledSelectField
              control={control}
              name="condition"
              id="material-group-condition"
              label="Condition"
              isRequired
              itemList={conditionOptions}
              errorMessage={errors.condition?.message}
            />
            <ControlledTextField
              control={control}
              name="quantity"
              id="material-group-quantity"
              label="Quantity"
              type="number"
              errorMessage={errors.quantity?.message as string | undefined}
              inputProps={{ "data-testid": "material-group-quantity-input" }}
            />
            <ControlledTextField
              control={control}
              name="minimumQuantity"
              id="material-group-min-quantity"
              label="Minimum Quantity"
              type="number"
              errorMessage={
                errors.minimumQuantity?.message as string | undefined
              }
              inputProps={{
                "data-testid": "material-group-min-quantity-input",
              }}
            />
            <ControlledTextField
              control={control}
              name="cost"
              id="material-group-cost"
              label="Unit Cost"
              type="number"
              errorMessage={errors.cost?.message as string | undefined}
              inputProps={{ "data-testid": "material-group-cost-input" }}
            />
            <ControlledTextField
              control={control}
              name="department"
              id="material-group-department"
              label="Department"
              errorMessage={errors.department?.message}
              inputProps={{ "data-testid": "material-group-department-input" }}
            />
            <ControlledTextField
              control={control}
              name="location"
              id="material-group-location"
              label="Location"
              errorMessage={errors.location?.message}
              inputProps={{ "data-testid": "material-group-location-input" }}
            />
            <ControlledTextField
              control={control}
              name="supplier"
              id="material-group-supplier"
              label="Supplier"
              errorMessage={errors.supplier?.message}
              inputProps={{ "data-testid": "material-group-supplier-input" }}
            />
            <ControlledTextField
              control={control}
              name="description"
              id="material-group-description"
              label="Description"
              multiline
              rows={2}
              errorMessage={errors.description?.message}
              inputProps={{
                "data-testid": "material-group-description-input",
              }}
            />
            <ControlledTextField
              control={control}
              name="notes"
              id="material-group-notes"
              label="Notes"
              multiline
              rows={2}
              errorMessage={errors.notes?.message}
              inputProps={{ "data-testid": "material-group-notes-input" }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-material-group-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-material-group-button"
          >
            {isLoading
              ? isEdit
                ? "Saving..."
                : "Creating..."
              : isEdit
                ? "Save Changes"
                : "Create Material Group"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageMaterialGroupDialog;
